import React, { useEffect, useRef, useState } from 'react';
import { Input } from '../ui/input';
import { Spinner } from '../ui/spinner';
import { Search } from 'lucide-react';
import { gameDepartmentAPI } from '../../services/api';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;

// Debounced title search over the game catalog (dm.di_unique_games), with a
// dropdown of matches. Selecting a result clears the query and hands the
// game back to the parent — the box itself holds no notion of "the current
// game", so the parent stays the single source of truth for that.
export default function GameSearchBox({ onSelect, placeholder = 'Search for a game…' }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    abortRef.current?.abort();

    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setLoading(false);
      setOpen(false);
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(() => {
      const controller = new AbortController();
      abortRef.current = controller;
      gameDepartmentAPI.searchGames(trimmed, controller.signal)
        .then(res => { setResults(res.data?.games || []); setOpen(true); })
        .catch(err => { if (err.code !== 'ERR_CANCELED') setResults([]); })
        .finally(() => setLoading(false));
    }, DEBOUNCE_MS);

    return () => clearTimeout(debounceRef.current);
  }, [query]);

  function handleSelect(game) {
    setQuery('');
    setResults([]);
    setOpen(false);
    onSelect(game);
  }

  return (
    <div ref={boxRef} className="relative w-full max-w-[360px]">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-mute pointer-events-none" />
        <Input
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder={placeholder}
          className="pl-8"
        />
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2"><Spinner size="sm" className="h-3.5 w-3.5" /></div>
        )}
      </div>

      {open && (
        <div className="absolute z-10 mt-1 w-full max-h-[280px] overflow-y-auto rounded-lg border border-white/10 bg-surface-2 shadow-xl">
          {results.length === 0 ? (
            <p className="text-xs text-ink-dim px-3 py-3">
              {query.trim().length < MIN_QUERY_LENGTH ? 'Keep typing…' : 'No games found'}
            </p>
          ) : results.map(g => (
            <button
              key={g.unique_game_id}
              type="button"
              onClick={() => handleSelect(g)}
              className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-xs hover:bg-surface-3 transition-colors border-b border-white/5 last:border-0"
            >
              <span className="text-ink font-medium truncate">{g.game_title}</span>
              <span className="text-ink-dim shrink-0">{g.provider}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
